import React, { Children } from 'react';
import { CheckCircleIcon, TrendingUpIcon, UsersIcon, TargetIcon, ZapIcon } from 'lucide-react';
import { motion } from 'framer-motion';
interface IdealPartnerProfileProps {
  asHero?: boolean;
}
export function IdealPartnerProfile({
  asHero = false
}: IdealPartnerProfileProps) {
  const criteria = [{
    icon: TrendingUpIcon,
    title: 'Revenue & Scale',
    items: ['Generating at least $1M+ in annual revenue', 'Operating with 10+ reps or preparing for scale', 'Willing to invest in building a real recruiting infrastructure'],
    note: '(Smaller companies are not excluded — but system effectiveness increases with structure and volume.)'
  }, {
    icon: UsersIcon,
    title: 'Leadership Mindset',
    items: ['Understands that recruiting is a system, not a gamble', 'Willing to follow a proven framework consistently', 'Prepared to support rookies through the critical first 8 weeks', 'Values long-term outcomes over short-term hacks']
  }, {
    icon: TargetIcon,
    title: 'Growth Intent',
    items: ['Wants predictable, repeatable growth', 'Builds for sustainability, not just rapid expansion', 'Is ready to move beyond ad-hoc recruiting methods', 'Has a vision for organized team development']
  }, {
    icon: ZapIcon,
    title: 'Partnership Approach',
    items: ['Sees HIRE as a strategic partner — not a vendor', 'Values communication, data, and transparency', 'Is open to feedback and collaboration', 'Is committed to the process']
  }];
  const sectionClasses = asHero ? 'pt-16 pb-12 md:pt-24 md:pb-16 bg-gradient-to-br from-gray-50 to-white' : 'section-padding bg-white';
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section className={sectionClasses} data-id="element-610">
      <div className="container-custom" data-id="element-611">
        <motion.div className="max-w-4xl mx-auto text-center mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-612">
          {asHero && <motion.div className="inline-block bg-slate/10 text-slate px-4 py-2 rounded text-sm font-semibold mb-6" variants={itemVariants} data-id="element-613">
              Selective Partnerships
            </motion.div>}
          <motion.h1 className={asHero ? 'mb-6' : 'mb-6'} variants={itemVariants} data-id="element-614">
            {asHero ? 'Who We Work With' : 'Our Ideal Partner'}
          </motion.h1>
          <motion.p className="text-body-large leading-relaxed" variants={itemVariants} data-id="element-615">
            {asHero ? "We're selective for a reason. The HIRE system works best with door-to-door and direct sales organizations that are ready to treat recruiting as a long-term operating system — not a short-term fix." : "If this feels like your company, there's a strong chance we can help."}
          </motion.p>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-616">
          {criteria.map((category, index) => {
          const Icon = category.icon;
          return <motion.div key={index} className="bg-white rounded p-8 shadow-lg border-2 border-gray-200 hover:border-slate transition-colors" variants={itemVariants} whileHover={{
            scale: 1.02,
            transition: {
              duration: 0.2
            }
          }} data-id="element-617">
                <div className="flex items-center mb-6" data-id="element-618">
                  <div className="w-12 h-12 bg-slate/10 rounded flex items-center justify-center mr-4" data-id="element-619">
                    <Icon className="h-6 w-6 text-slate" data-id="element-620" />
                  </div>
                  <h3 data-id="element-621">{category.title}</h3>
                </div>
                <ul className="space-y-3" data-id="element-622">
                  {category.items.map((item, idx) => <li key={idx} className="flex items-start" data-id="element-623">
                      <CheckCircleIcon className="h-5 w-5 text-slate flex-shrink-0 mr-3 mt-0.5" data-id="element-624" />
                      <span data-id="element-625">{item}</span>
                    </li>)}
                </ul>
                {category.note && <p className="text-sm italic mt-4" data-id="element-626">{category.note}</p>}
              </motion.div>;
        })}
        </motion.div>

        <motion.div className="mt-12 text-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.4
      }} data-id="element-627">
          <div className="inline-block bg-slate/10 border-l-4 border-slate px-8 py-6 rounded max-w-3xl mb-6" data-id="element-628">
            <p className="text-body-large" data-id="element-629">
              <span className="font-bold text-navy" data-id="element-630">
                Not sure if you're a fit?
              </span>{' '}
              That's exactly what the strategy call is for. We'll be honest with
              you either way.
            </p>
          </div>
          <br data-id="element-631" />
          <a href="/book-call" className="inline-block bg-slate hover:bg-slate-dark text-white font-bold px-8 py-4 rounded transition-colors shadow-lg" data-id="element-632">
            Schedule a Strategy Call →
          </a>
        </motion.div>
      </div>
    </section>;
}